import { RootState } from "./index.ts";
import { createSelector } from "@reduxjs/toolkit";

//// Housings ////

export const selectHousingsReducer = (state: RootState) => state.housingsReducer;

export const selectHousings = (state: RootState) =>
    selectHousingsReducer(state)?.housings;
export const selectHousingsPending = (state: RootState) => selectHousings(state)?.pending;
export const selectHousingsError = (state: RootState) => selectHousings(state)?.error;
export const selectHousingsList = (state: RootState) => selectHousings(state)?.data;

//////////////////////////

//// Shared data ////

export const selectShared = (state: RootState) => selectHousingsReducer(state)?.sharedData;

export const selectSharedPending = (state: RootState) => selectShared(state)?.pending;
export const selectSharedError = (state: RootState) => selectShared(state)?.error;
export const selectSharedData = createSelector([selectShared], (shared) => {
    const data = shared?.data;

    if (!data) {
        return data;
    }

    if (!data.content) {
        return data;
    }

    return {
        ...data,
        ...data.content,
        housingData: data.content?.housingData ?? data.housingData,
    };
});

export const selectSharedHousingData = createSelector([selectSharedData], (data) =>
    data?.housingData,
);

//////////////////////////

//// Sharing data ////

export const selectSharing = (state: RootState) => selectHousingsReducer(state)?.sharingData;

export const selectSharingPending = (state: RootState) => selectSharing(state)?.pending;
export const selectSharingError = (state: RootState) => selectSharing(state)?.error;
export const selectSharingDataId = (state: RootState) => selectSharing(state)?.id;
export const selectSharingDataCode = (state: RootState) => selectSharing(state)?.code;

//////////////////////////

//// InstallmentPlans ////

export const selectInstallmentPlanTypes = (state: RootState) =>
    selectHousingsReducer(state)?.installmentPlanTypes;
export const selectInstallmentPlanTypesPending = (state: RootState) =>
    selectInstallmentPlanTypes(state)?.pending;
export const selectInstallmentPlanTypesError = (state: RootState) =>
    selectInstallmentPlanTypes(state)?.error;
export const selectInstallmentPlanTypesList = (state: RootState) =>
    selectInstallmentPlanTypes(state)?.data;

export const selectPricesByFirstPayment = (state: RootState) =>
    selectHousingsReducer(state)?.housingPricesByFirstPayment;
export const selectPricesByFirstPaymentPending = (state: RootState) =>
    selectPricesByFirstPayment(state)?.pending;
export const selectPricesByFirstPaymentError = (state: RootState) =>
    selectPricesByFirstPayment(state)?.error;
export const selectPricesByFirstPaymentData = (state: RootState) =>
    selectPricesByFirstPayment(state)?.data;
export const selectRequiredPayment = (state: RootState) =>
    selectPricesByFirstPaymentData(state)?.requiredPayment;

export const selectPricesByPeriod = (state: RootState) =>
    selectHousingsReducer(state)?.housingPricesByPeriod;
export const selectPricesByPeriodPending = (state: RootState) =>
    selectPricesByPeriod(state)?.pending;
export const selectPricesByPeriodError = (state: RootState) =>
    selectPricesByPeriod(state)?.error;
export const selectPricesByPeriodData = (state: RootState) => selectPricesByPeriod(state)?.data;

//////////////////////////

//// User ////

export const selectUserReducer = (state: RootState) => state.userReducer;

export const selectUserPending = (state: RootState) => selectUserReducer(state).pending;
export const selectUserError = (state: RootState) => selectUserReducer(state).error;
export const selectUserErrorMessage = (state: RootState) => selectUserReducer(state).errorMessage;
export const selectUserData = (state: RootState) => selectUserReducer(state).userData.data;

export const selectUserContacts = (state: RootState) => selectUserReducer(state)?.contacts;

export const selectUserContactsPending = (state: RootState) =>
    selectUserReducer(state)?.contacts?.pending ?? false;

export const selectUserContactsError = (state: RootState) =>
    selectUserReducer(state)?.contacts?.error ?? false;

export const selectUserContactsData = (state: RootState) =>
    selectUserReducer(state)?.contacts?.data ?? [];

/** Есть ли уже непустые контакты */
export const selectUserContactsHasData = createSelector([selectUserContactsData], (data) =>
    data.some((c) => (c?.value ?? "").toString().trim().length > 0),
);

/** Нужно ли грузить: только если нет активной загрузки и нет данных */
export const selectUserContactsShouldFetch = createSelector(
    [selectUserContactsPending, selectUserContactsHasData],
    (pending, hasData) => !pending && !hasData,
);

/** Показывать ли ошибку: только если нет загрузки и нет данных */
export const selectUserContactsShowError = createSelector(
    [selectUserContactsPending, selectUserContactsError, selectUserContactsHasData],
    (pending, error, hasData) => !pending && error && !hasData,
);

// утилита поиска значения по типу (с приоритетом непустых)
const getValueByType = (data: Array<{ type: string; value: string }>, type: string) => {
    const found = data.find((c) => c?.type === type);
    const val = found?.value;
    return val && String(val).trim().length > 0 ? String(val).trim() : null;
};

// точечные селекторы
export const selectTelegram = createSelector([selectUserContactsData], (data) =>
    getValueByType(data, "telegram"),
);

export const selectWhatsapp = createSelector([selectUserContactsData], (data) =>
    getValueByType(data, "whatsapp"),
);

export const selectInstagram = createSelector([selectUserContactsData], (data) =>
    getValueByType(data, "instagram"),
);

/** Фабрика: получить контакт по произвольному типу */
export const makeSelectContactByType = (type: string) =>
    createSelector([selectUserContactsData], (data) => getValueByType(data, type));

export const selectUpdateProfileState = (state: RootState) =>
    selectUserReducer(state)?.updateProfile ?? {
        pending: false,
        error: false,
        errorMessage: null,
        success: false,
    };

export const selectUpdateProfilePending = createSelector([selectUpdateProfileState], (s) => !!s.pending);
export const selectUpdateProfileError = createSelector([selectUpdateProfileState], (s) => !!s.error);
export const selectUpdateProfileSuccess = createSelector([selectUpdateProfileState], (s) => !!s.success);
export const selectUpdateProfileErrorMessage = createSelector(
    [selectUpdateProfileState],
    (s) => s.errorMessage ?? null,
);

export const selectUpdateProfileFieldErrors = (state: RootState) =>
    state.updateProfile?.fieldErrors || {};

export const makeSelectUpdateProfileFieldError = (field: string) =>
    createSelector([selectUpdateProfileFieldErrors], (errs) => errs?.[field] ?? null);

export const selectIsAuth = (state: RootState) => !!selectUserData(state).token;
export const selectUserToken = (state: RootState) => selectUserData(state).token;

export const selectUserInfo = createSelector([selectUserData], (data) => {
    if (!data) {
        return null;
    }

    if (data.user) {
        return data.user;
    }

    if (data.name || data.id || data.email || data.phone) {
        return data;
    }

    return null;
});

export const selectIsEmailVerified = (state: RootState) => selectUserInfo(state)?.emailVerified;
export const selectIsPaid = (state: RootState) => selectUserInfo(state)?.paid;
export const selectPaidTill = (state: RootState) => selectUserInfo(state)?.paidTill;

export const selectIsTestPeriod = (state: RootState) =>
    selectUserInfo(state)?.subscription?.isTrial ?? selectUserInfo(state)?.testPeriod;
export const selectTestPeriodTill = (state: RootState) => selectUserInfo(state)?.testPeriodTill;

export const selectIsRememberMe = (state: RootState) => selectUserReducer(state)?.rememberMe;

export const selectResetPassword = (state: RootState) => selectUserReducer(state)?.resetPassword;
export const selectResetPasswordPending = (state: RootState) => selectResetPassword(state)?.pending;
export const selectResetPasswordError = (state: RootState) => selectResetPassword(state)?.error;
export const selectResetPasswordErrorMessage = (state: RootState) =>
    selectResetPassword(state)?.errorMessage;
export const selectResetPasswordSuccess = (state: RootState) => selectResetPassword(state)?.success;

export const selectSubscriptionPlansBlock = (state: RootState) =>
    selectUserReducer(state)?.subscriptionPlans;
export const selectSubscriptionPlansPending = (state: RootState) =>
    selectSubscriptionPlansBlock(state)?.pending;
export const selectSubscriptionPlansError = (state: RootState) =>
    selectSubscriptionPlansBlock(state)?.error;
export const selectSubscriptionPlansList = (state: RootState) =>
    selectSubscriptionPlansBlock(state)?.data;

export const selectSubscription = (state: RootState) => selectUserInfo(state)?.subscription;
export const selectSubscriptionExpiresAt = (state: RootState) => selectSubscription(state)?.expiresAt;
export const selectSubscriptionLevel = (state: RootState) => selectSubscription(state)?.code;

//////////////////////////



//// Verify Email ////

export const selectVerifyEmail = (state: RootState) => selectUserReducer(state)?.verifyEmail;

export const selectVerifyEmailPending = (state: RootState) => selectVerifyEmail(state)?.pending;
export const selectVerifyEmailError = (state: RootState) => selectVerifyEmail(state)?.error;
export const selectVerifyEmailErrorKind = (state: RootState) => selectVerifyEmail(state)?.errorKind;
export const selectVerifyEmailSuccess = (state: RootState) => selectVerifyEmail(state)?.success;

//////////////////////////

//// Notification ////

export const selectNotification = (state: RootState) => selectUserReducer(state)?.notification;

export const selectNotificationIsOpen = (state: RootState) => selectNotification(state)?.isOpen;
export const selectNotificationValue = (state: RootState) => selectNotification(state)?.value;

//////////////////////////

//// Payment ////

export const selectPayment = (state: RootState) => selectUserReducer(state)?.payment;

export const selectPaymentPending = (state: RootState) => selectPayment(state)?.pending;
export const selectPaymentError = (state: RootState) => selectPayment(state)?.error;
export const selectPaymentErrorMessage = (state: RootState) => selectPayment(state)?.errorMessage;
export const selectPaymentSuccess = (state: RootState) => selectPayment(state)?.success;
export const selectPaymentConfirmationUrl = (state: RootState) => selectPayment(state)?.confirmationUrl;

//////////////////////////

//// Commerce Housings ////

export const selectCommerceHousings = (state: RootState) =>
    selectHousingsReducer(state)?.commerceHousings;

export const selectCommerceHousingsPending = (state: RootState) =>
    selectCommerceHousings(state)?.pending;

export const selectCommerceHousingsError = (state: RootState) => selectCommerceHousings(state)?.error;

export const selectCommerceHousingsList = (state: RootState) => selectCommerceHousings(state)?.data;

//////////////////////////

//// Commerce InstallmentPlans ////

export const selectCommerceInstallmentPlanTypes = (state: RootState) =>
    selectHousingsReducer(state)?.commerceInstallmentPlanTypes;

export const selectCommerceInstallmentPlanTypesPending = (state: RootState) =>
    selectCommerceInstallmentPlanTypes(state)?.pending;

export const selectCommerceInstallmentPlanTypesError = (state: RootState) =>
    selectCommerceInstallmentPlanTypes(state)?.error;

export const selectCommerceInstallmentPlanTypesList = (state: RootState) =>
    selectCommerceInstallmentPlanTypes(state)?.data;

//////////////////////////

//// Commerce Prices by First Payment ////

export const selectCommercePricesByFirstPayment = (state: RootState) =>
    selectHousingsReducer(state)?.commercePricesByFirstPayment;

export const selectCommercePricesByFirstPaymentPending = (state: RootState) =>
    selectCommercePricesByFirstPayment(state)?.pending;

export const selectCommercePricesByFirstPaymentError = (state: RootState) =>
    selectCommercePricesByFirstPayment(state)?.error;

export const selectCommercePricesByFirstPaymentData = (state: RootState) =>
    selectCommercePricesByFirstPayment(state)?.data;

export const selectCommerceRequiredPayment = (state: RootState) =>
    selectCommercePricesByFirstPaymentData(state)?.requiredPayment ?? null;

//////////////////////////

//// Commerce Prices by Period ////

export const selectCommercePricesByPeriod = (state: RootState) =>
    selectHousingsReducer(state)?.commercePricesByPeriod;

export const selectCommercePricesByPeriodPending = (state: RootState) =>
    selectCommercePricesByPeriod(state)?.pending;

export const selectCommercePricesByPeriodError = (state: RootState) =>
    selectCommercePricesByPeriod(state)?.error;

export const selectCommercePricesByPeriodData = (state: RootState) =>
    selectCommercePricesByPeriod(state)?.data;

// ─────────────────────────────── CREATE CLIENT (клиентская база)

export const selectCreateClientState = (state: RootState) =>
    selectUserReducer(state)?.createClient ?? {
        pending: false,
        error: false,
        errorMessage: null,
        success: false,
        fieldErrors: {},
        data: null,
    };

export const selectCreateClientPending = createSelector([selectCreateClientState], (s) => !!s.pending);
export const selectCreateClientError = createSelector([selectCreateClientState], (s) => !!s.error);
export const selectCreateClientSuccess = createSelector([selectCreateClientState], (s) => !!s.success);
export const selectCreateClientErrorMessage = createSelector(
    [selectCreateClientState],
    (s) => s.errorMessage ?? null,
);
export const selectCreateClientFieldErrors = createSelector([selectCreateClientState], (s) => s.fieldErrors ?? {});
export const selectCreateClientData = createSelector([selectCreateClientState], (s) => s.data ?? null);

// 🆕 ─────────────────────────────── CLIENTS BASE (список клиентов)

export const selectClientsBaseState = (state: RootState) =>
    selectUserReducer(state)?.clientsBase ?? {
        pending: false,
        loadingMore: false,
        error: false,
        errorMessage: null,
        data: [],
        meta: {
            perPage: 20,
            nextCursor: null,
            hasMore: false,
        },
    };

export const selectClientsBasePending = createSelector([selectClientsBaseState], (s) => !!s.pending);
export const selectClientsBaseError = createSelector([selectClientsBaseState], (s) => !!s.error);
export const selectClientsBaseErrorMessage = createSelector(
    [selectClientsBaseState],
    (s) => s.errorMessage ?? null,
);
export const selectClientsBaseList = createSelector([selectClientsBaseState], (s) => s.data ?? []);
export const selectClientsBaseLoadingMore = createSelector(
    [selectClientsBaseState],
    (s) => !!s.loadingMore,
);
export const selectClientsBaseMeta = createSelector(
    [selectClientsBaseState],
    (s) => s.meta ?? {
        perPage: 20,
        nextCursor: null,
        hasMore: false,
        periodFrom: null,
        periodTo: null,
    },
);

export const selectClientDealsState = (state: RootState) => selectUserReducer(state)?.clientDeals ?? {};

export const selectClientDealsPending = (state: RootState) => selectClientDealsState(state)?.pending ?? false;
export const selectClientDealsError = (state: RootState) => selectClientDealsState(state)?.error ?? false;
export const selectClientDealsErrorMessage = (state: RootState) => selectClientDealsState(state)?.errorMessage ?? null;
export const selectClientDealsData = (state: RootState) => selectClientDealsState(state)?.data ?? {};
export const selectClientDealsLoadingId = (state: RootState) => selectClientDealsState(state)?.currentLoadingId ?? null;

export const selectCreateDealState = (state: RootState) =>
    selectUserReducer(state)?.createDeal ?? {
        pending: false,
        error: false,
        errorMessage: null,
    };

export const selectCreateDealPending = (state: RootState) => selectCreateDealState(state).pending;
export const selectCreateDealErrorMessage = (state: RootState) => selectCreateDealState(state).errorMessage;

export const selectUpdateDealState = (state: RootState) =>
    selectUserReducer(state)?.updateDeal ?? {
        pending: false,
        error: false,
        errorMessage: null,
    };

export const selectUpdateDealPending = (state: RootState) => selectUpdateDealState(state).pending;
export const selectUpdateDealErrorMessage = (state: RootState) => selectUpdateDealState(state).errorMessage;

export const selectAddDealCommentState = (state: RootState) =>
    selectUserReducer(state)?.addDealComment ?? { pending: false, error: false, errorMessage: null };

export const selectAddDealCommentPending = (state: RootState) => selectAddDealCommentState(state).pending;
export const selectAddDealCommentErrorMessage = (state: RootState) => selectAddDealCommentState(state).errorMessage;

// 🆕 ─────────────────────────────── DEAL STATUSES (каталог + счётчики)

export const selectDealStatusesState = (state: RootState) => selectUserReducer(state)?.dealStatuses;

export const selectDealStatusesPending = createSelector([selectDealStatusesState], (s) => !!s?.pending);
export const selectDealStatusesError = createSelector([selectDealStatusesState], (s) => !!s?.error);
export const selectDealStatusesErrorMessage = createSelector(
    [selectDealStatusesState],
    (s) => s?.errorMessage ?? null,
);

/**
 * Сырые статусы из бэка: [{ id, code, name, color, count }, ...]
 */
export const selectDealStatusesRawList = createSelector([selectDealStatusesState], (s) =>
    Array.isArray(s?.data) ? s!.data : [],
);

/**
 * Общий total из респонса бэка (сумма count по статусам)
 */
export const selectDealStatusesTotal = createSelector([selectDealStatusesState], (s) =>
    typeof s?.total === "number" ? s.total : null,
);

/**
 * Каталог для UI в формате DealStatusCatalog:
 * [{ id, code, name, color }, ...]
 */
export const selectDealStatusCatalog = createSelector([selectDealStatusesRawList], (list) =>
    list.map((item) => ({
        id: item.id,
        code: item.code,
        name: item.name,
        color: item.color,
    })),
);

/**
 * Мапа "id статуса → количество сделок"
 * { "7590845760000001": 1, "7590845760000002": 1, ... }
 */
export const selectDealStatusesCountsById = createSelector([selectDealStatusesRawList], (list) =>
    list.reduce(
        (acc, item) => {
            acc[String(item.id)] = item.count ?? 0;
            return acc;
        },
        {} as Record<string, number>,
    ),
);

/**
 * Мапа "code статуса → количество сделок"
 * { "contact": 1, "need": 1, "waiting_money": 1, ... }
 */
export const selectDealStatusesCountsByCode = createSelector([selectDealStatusesRawList], (list) =>
    list.reduce(
        (acc, item) => {
            acc[item.code] = item.count ?? 0;
            return acc;
        },
        {} as Record<string, number>,
    ),
);

export * from './Admin/selectors';
